<template>
  <div class="flex flex-col gap-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">过程检查</h1>
        <p class="text-sm text-slate-500 mt-1">追踪项目阶段性进展，智能管控任务进度与经费执行风险</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">第 4 环节</span>
        <span class="px-3 py-1.5 bg-amber-50 text-amber-600 rounded-full text-xs font-semibold">进行中</span>
      </div>
    </div>

    <!-- Segmented Control -->
    <div class="flex justify-center">
      <div class="segmented-control">
        <button
          class="segmented-btn"
          :class="{ 'active': activeTab === 'task' }"
          @click="activeTab = 'task'"
        >任务书分析</button>
        <button
          class="segmented-btn"
          :class="{ 'active': activeTab === 'execution' }"
          @click="activeTab = 'execution'"
        >项目执行情况分析</button>
      </div>
    </div>

    <!-- Task Book Analysis Tab -->
    <div v-if="activeTab === 'task'" class="page-section active">
      <div class="glass-card rounded-3xl p-6">
        <div class="flex items-center gap-2 mb-6">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">任务书与指南/合同分析</h2>
        </div>

        <p class="text-sm text-slate-500 mb-4">对比任务书与指南/立项合同，分析是否存在修改内容/指标的情况</p>

        <div class="mb-6">
          <label class="block text-sm font-semibold text-slate-700 mb-2">任务书编号</label>
          <textarea
            v-model="taskIds"
            class="premium-input w-full h-32 rounded-2xl p-5 text-sm leading-relaxed resize-none"
            placeholder="请输入任务书编号，多个编号请用换行分隔..."
          ></textarea>
          <div class="mt-2 text-xs text-slate-400 text-right">
            {{ taskIds.split('\n').filter(id => id.trim()).length }} 个任务书
          </div>
        </div>

        <div class="flex justify-center">
          <button class="btn-action px-10 py-4 rounded-2xl text-white font-bold text-base flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
            </svg>
            启动任务书分析
          </button>
        </div>
      </div>

      <!-- Analysis Results -->
      <div class="glass-card rounded-3xl p-6">
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <h2 class="text-lg font-bold text-slate-800">分析结果</h2>
          </div>
          <button class="btn-export px-4 py-2 rounded-xl text-white text-sm font-medium flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
            </svg>
            导出报告
          </button>
        </div>

        <div class="space-y-4">
          <div class="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <div class="flex-1">
                <h4 class="font-semibold text-emerald-800 text-sm">研究内容一致性：无修改</h4>
                <p class="text-xs text-emerald-600 mt-1">任务书研究内容与指南要求完全一致，无修改情况</p>
              </div>
            </div>
          </div>

          <div class="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
              </div>
              <div class="flex-1">
                <h4 class="font-semibold text-amber-800 text-sm">考核指标调整：部分调整</h4>
                <p class="text-xs text-amber-600 mt-1">任务书中部分考核指标进行了细化调整，建议关注调整合理性</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Execution Analysis Tab -->
    <div v-if="activeTab === 'execution'" class="page-section active">
      <!-- 执行分析维度 -->
      <div class="glass-card rounded-3xl p-6 mb-6">
        <div class="flex items-center gap-2 mb-6">
          <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
          </svg>
          <h2 class="text-base font-bold text-slate-800">执行分析维度</h2>
        </div>

        <!-- Analysis Dimension Cards -->
        <div class="grid grid-cols-2 gap-4 mb-6">
          <!-- 任务执行情况分析 -->
          <label class="relative cursor-pointer">
            <input type="checkbox" class="sr-only" v-model="executionDimensions" value="task">
            <div class="p-5 rounded-2xl border-2 transition-all duration-300 h-full" 
                 :class="executionDimensions.includes('task') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start justify-between">
                <div class="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                </div>
                <div v-if="executionDimensions.includes('task')" class="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <div v-else class="w-6 h-6 rounded-full border-2 border-slate-300"></div>
              </div>
              <h3 class="text-base font-semibold text-blue-600 mt-4">任务执行情况分析</h3>
              <p class="text-sm text-slate-500 mt-2">对标原始任务书，评估阶段性科研目标的完成进度与质量。</p>
            </div>
          </label>

          <!-- 经费执行情况分析 -->
          <label class="relative cursor-pointer">
            <input type="checkbox" class="sr-only" v-model="executionDimensions" value="funding">
            <div class="p-5 rounded-2xl border-2 transition-all duration-300 h-full"
                 :class="executionDimensions.includes('funding') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start justify-between">
                <div class="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div v-if="executionDimensions.includes('funding')" class="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <div v-else class="w-6 h-6 rounded-full border-2 border-slate-300"></div>
              </div>
              <h3 class="text-base font-semibold text-blue-600 mt-4">经费执行情况分析</h3>
              <p class="text-sm text-slate-500 mt-2">核算科目支出明细，预警不合理预算分配及超支风险。</p>
            </div>
          </label>
        </div>

        <!-- 专项计划选择 & 待分析文件编号 -->
        <div class="grid grid-cols-2 gap-6 mb-6">
          <!-- 专项计划选择 -->
          <div>
            <div class="flex items-center gap-2 mb-2">
              <span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
              <label class="text-sm font-semibold text-slate-700">专项计划选择</label>
            </div>
            <div class="relative">
              <select v-model="selectedPlan" class="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 appearance-none cursor-pointer focus:outline-none focus:border-blue-500">
                <option value="">请选择进行分析的专项</option>
                <option value="annual">年度重点专项</option>
                <option value="key">关键核心技术专项</option>
                <option value="basic">基础研究专项</option>
              </select>
              <svg class="w-5 h-5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </div>
          </div>

          <!-- 待分析文件编号 -->
          <div>
            <div class="flex items-center gap-2 mb-2">
              <span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
              <label class="text-sm font-semibold text-slate-700">待分析文件编号</label>
            </div>
            <input 
              v-model="fileNumber"
              type="text" 
              class="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:border-blue-500"
              placeholder="输入中期检查或年度报告文件编号..."
            >
          </div>
        </div>

        <!-- 文件上传区 -->
        <div class="mb-6">
          <div class="flex items-center gap-2 mb-2">
            <span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
            <label class="text-sm font-semibold text-slate-700">文件上传区</label>
          </div>
          <div class="border-2 border-dashed border-slate-300 rounded-2xl p-10 text-center hover:border-blue-400 hover:bg-blue-50/20 transition-all cursor-pointer">
            <svg class="w-12 h-12 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
            </svg>
            <p class="text-sm text-slate-600 font-medium">点击或拖拽中期检查/年度报告文件至此处</p>
            <p class="text-xs text-slate-400 mt-1">支持 PDF、DOCX 格式分析</p>
          </div>
        </div>

        <!-- 启动按钮 -->
        <div class="flex justify-center">
          <button class="px-8 py-3.5 bg-slate-800 hover:bg-slate-900 rounded-xl text-white font-semibold text-sm flex items-center gap-2 transition-colors">
            启动执行情况分析引擎
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- 分析结果 -->
      <div class="glass-card rounded-3xl p-6">
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <h2 class="text-lg font-bold text-slate-800">审查结果</h2>
          </div>
          <button class="btn-export px-4 py-2 rounded-xl text-white text-sm font-medium flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
            </svg>
            导出报告
          </button>
        </div>

        <h3 class="text-xl font-bold text-slate-800 mb-4">项目执行情况分析</h3>

        <div class="space-y-4">
          <!-- 任务执行情况结果 -->
          <div class="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <div class="flex-1">
                <h4 class="font-semibold text-emerald-800 text-sm">任务执行情况：正常</h4>
                <p class="text-xs text-emerald-600 mt-1">项目阶段性目标完成情况良好，关键里程碑按计划推进，无延期风险。</p>
              </div>
            </div>
          </div>

          <!-- 经费执行情况结果 -->
          <div class="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
              </div>
              <div class="flex-1">
                <h4 class="font-semibold text-amber-800 text-sm">经费执行情况：预警</h4>
                <p class="text-xs text-amber-600 mt-1">设备费支出进度偏慢，建议加快采购流程；劳务费使用符合预算计划。</p>
              </div>
            </div>
          </div>

          <!-- 具体问题 -->
          <div class="ml-4">
            <h5 class="font-semibold text-slate-700 text-sm mb-3">具体问题：</h5>
            <ul class="space-y-2 text-sm text-slate-600">
              <li class="flex items-start gap-2">
                <span class="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0"></span>
                <span>设备费预算执行率仅35%，低于预期进度（50%）</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0"></span>
                <span>测试化验加工费部分支出缺少详细验收单据</span>
              </li>
            </ul>
          </div>

          <!-- 处理建议 -->
          <div class="p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div class="flex items-center gap-2 mb-3">
              <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
              </svg>
              <h4 class="font-semibold text-blue-800 text-sm">处理建议</h4>
            </div>
            <ol class="space-y-2 text-sm text-blue-700 ml-6 list-decimal">
              <li>加快设备采购流程，确保年底前完成主要设备购置</li>
              <li>补充完善测试化验加工费的验收单据和结算凭证</li>
              <li>建议每月定期跟踪经费执行进度，及时调整预算分配</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProcessCheck',
  data() {
    return {
      activeTab: 'task',
      taskIds: '',
      executionDimensions: ['task', 'funding'],
      selectedPlan: '',
      fileNumber: ''
    }
  },
  mounted() {
    // Check for tab query parameter
    const tab = this.$route.query.tab
    if (tab === 'execution') {
      this.activeTab = 'execution'
    } else if (tab === 'task') {
      this.activeTab = 'task'
    }
  },
  watch: {
    '$route.query.tab'(newTab) {
      if (newTab === 'execution') {
        this.activeTab = 'execution'
      } else if (newTab === 'task') {
        this.activeTab = 'task'
      }
    }
  }
}
</script>
